import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import { Marquee } from "../components/Marquee";
import { SocialLinks } from "../components/SocialLinks";

const HERO = "https://images.unsplash.com/photo-1550547660-d9450f859349?auto=format&fit=crop&w=1400&q=80";
const LIFE = "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?auto=format&fit=crop&w=1000&q=80";

export const Home = () => {
  const { t, pick, lang } = useLang();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {}); }, []);

  const home = settings.home || {};
  const hv = (key, def) => {
    const v = lang === "ms" ? (home[`${key}_ms`] || home[`${key}_en`]) : (home[`${key}_en`] || home[`${key}_ms`]);
    return v || def;
  };
  const heroImg = home.hero_image ? fileUrl(home.hero_image) : HERO;
  const storyImg = home.story_image ? fileUrl(home.story_image) : LIFE;

  const imgOf = (p) => p.image_path ? fileUrl(p.image_path) : HERO;

  return (
    <div>
      {settings.promo_enabled && (settings.promo_text || settings.promo_text_ms) && (
        settings.promo_link ? (
          <a href={settings.promo_link} data-testid="promo-banner" className="block bg-brand-ink px-5 py-2.5 text-center text-sm font-bold text-brand-yellow transition-colors hover:bg-brand-forest">
            🔥 {lang === "ms" ? (settings.promo_text_ms || settings.promo_text) : (settings.promo_text || settings.promo_text_ms)}
          </a>
        ) : (
          <div data-testid="promo-banner" className="bg-brand-ink px-5 py-2.5 text-center text-sm font-bold text-brand-yellow">
            🔥 {lang === "ms" ? (settings.promo_text_ms || settings.promo_text) : (settings.promo_text || settings.promo_text_ms)}
          </div>
        )
      )}
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-green grain">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <div className="fade-up relative z-10">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> {hv("hero_eyebrow", t("hero_eyebrow"))}
            </span>
            <h1 className="mt-6 font-display text-5xl font-black leading-none tracking-tighter text-brand-ink sm:text-7xl">
              {hv("hero_title", t("hero_title"))}
            </h1>
            <p className="mt-6 max-w-md text-lg text-brand-ink/80">{hv("hero_sub", t("hero_sub"))}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-menu-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-ink px-6 py-3 font-bold text-brand-yellow hover-brutal">
                {hv("hero_cta_menu", t("hero_cta_menu"))} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-join-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-white px-6 py-3 font-bold text-brand-ink hover-brutal">
                {hv("hero_cta_join", t("hero_cta_join"))}
              </Link>
            </div>
            <div className="mt-8">
              <SocialLinks settings={settings} />
            </div>
          </div>
          <div className="fade-up relative z-10">
            <img src="/panda-chef.png" alt="" className="absolute -left-10 -top-14 z-20 hidden h-36 w-36 -rotate-6 drop-shadow-xl sm:block" />
            <img src="/handmade-stamp.png" alt="Handmade burgers" className="absolute -right-6 -top-8 z-20 h-28 w-28 rotate-12" />
            <div className="overflow-hidden rounded-3xl border-2 border-brand-ink shadow-brutal-lg">
              <img src={heroImg} alt="Panda Tata burger" className="aspect-[4/3] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 rotate-[-6deg] rounded-2xl border-2 border-brand-ink bg-brand-yellow px-5 py-3 font-display text-xl font-semibold shadow-brutal">
              {hv("hero_badge", "RM1 = 1 Point 🍔")}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PRODUCTS */}
      <section className="relative mx-auto max-w-7xl px-5 py-16">
        <img src="/fresh-brush.png" alt="" className="pointer-events-none absolute right-4 top-6 hidden h-16 w-auto -rotate-6 sm:block" />
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-brand-greendark">{hv("products_sub", t("home_products_sub"))}</p>
            <h2 className="mt-1 font-display text-4xl font-bold tracking-tight sm:text-5xl">{hv("products_title", t("home_products_title"))}</h2>
          </div>
          <Link to="/products" className="hidden items-center gap-1 text-sm font-bold text-brand-greendark hover:underline sm:flex">
            {t("home_view_all")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.length === 0 && <p className="text-muted-foreground">Coming soon…</p>}
          {products.map((p) => (
            <div key={p.id} className="group overflow-hidden rounded-3xl border-2 border-brand-ink bg-white hover-brutal">
              <div className="overflow-hidden">
                <img src={imgOf(p)} alt={pick(p, "name")} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-2xl font-bold">{pick(p, "name")}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{pick(p, "description")}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* STORY / FRANCHISE bento */}
      <section className="mx-auto max-w-7xl px-5 pb-16">
        <div className="grid gap-6 lg:grid-cols-2">
          <Link to="/story" className="group relative overflow-hidden rounded-3xl border-2 border-brand-ink hover-brutal">
            <img src={storyImg} alt="Story" className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <h3 className="font-display text-3xl font-bold text-white">{hv("story_title", t("home_story_title"))}</h3>
              <span className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-brand-yellow">{t("read_more")} <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
          <div className="relative flex flex-col justify-between rounded-3xl border-2 border-brand-ink bg-brand-yellow p-8 shadow-brutal">
            <img src="/panda-excited.png" alt="" className="pointer-events-none absolute -right-5 -top-12 h-28 w-28 rotate-6 drop-shadow-lg" />
            <div>
              <h3 className="font-display text-3xl font-bold sm:text-4xl">{hv("franchise_title", t("home_franchise_title"))}</h3>
              <p className="mt-3 max-w-sm text-brand-ink/80">{hv("franchise_sub", t("home_franchise_sub"))}</p>
            </div>
            <Link to="/franchise" data-testid="home-franchise-btn" className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-ink px-6 py-3 font-bold text-brand-yellow hover-brutal">
              {hv("franchise_cta", t("home_franchise_cta"))} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
